import readXlsxFile from "read-excel-file";
import { companyApi } from "../../api/company-api";
import { CreateDeliveryPayload } from "../../types/delivery/delivery-payload";
import { CheckedExcelData, ExcelData } from "../../types/excel/excel-data";
import { parseLogisticsTypeCell } from "../../types/delivery/logistics-type";
import { HelperText } from "../../types/helper-text";
import { formatDate } from "./format";
import { removeInvalidSpecialCharacters } from "./remove-invalid-special-character";

const parseDateRange = (dateStr: string) => {
    try {
        // "2024.7.25~2024.7.28" 형식의 문자열을 파싱
        const [startStr, endStr] = dateStr.split('~').map(d => d.trim());

        // 각 날짜 문자열을 Date 객체로 변환
        const startDate = new Date(startStr.replace(/\./g, '-'));
        const endDate = new Date(endStr.replace(/\./g, '-'));

        // 유효한 날짜인지 확인
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            throw new Error('Invalid date format');
        }

        return { startDate, endDate };
    } catch (error) {
        // 범위가 아닌 단일 날짜인 경우
        const singleDate = new Date(String(dateStr));
        if (!isNaN(singleDate.getTime())) {
            return { startDate: singleDate, endDate: singleDate };
        }
        throw error;
    }
};

export const deliveryErrorCheck = async (
    excelDataArray: ExcelData[],
    apiResponseDataStore: HelperText,
    rowIndex: number
) => {
    const checkExcelSheetData: CheckedExcelData[] = [];
    const createDeliveryRequestData: CreateDeliveryPayload[] = [];

    const setClient = async (clientName: string) => {
        try {
            if (
                apiResponseDataStore === null ||
                !(clientName in apiResponseDataStore)
            ) {
                const response = await companyApi.getByName(clientName);
                apiResponseDataStore[`${clientName}`] = response.data.id;
            } else if (!apiResponseDataStore[`${clientName}`]) {
                apiResponseDataStore[`${clientName}`] = "error";
            }
        } catch (error) {
            apiResponseDataStore[`${clientName}`] = "error";
            console.log(error, "error");
        }
    };

    const setAgency = async (agentName: string) => {
        try {
            if (
                apiResponseDataStore === null ||
                !(agentName in apiResponseDataStore)
            ) {
                const response = await companyApi.getByName(agentName);
                apiResponseDataStore[`${agentName}`] = response.data.id;
            } else if (!apiResponseDataStore[`${agentName}`]) {
                apiResponseDataStore[`${agentName}`] = "error";
            }
        } catch (error) {
            apiResponseDataStore[`${agentName}`] = "error";
            console.log(error, "error");
        }
    };

    for (const excelData of excelDataArray) {
        const data = await readXlsxFile(excelData.file);
        const clientName = String(data[0][1]);
        const agencyName = String(data[0][5]);

        await setClient(clientName);
        await setAgency(agencyName);
    }

    for (const [index, value] of excelDataArray.entries()) {
        const excelData = value;
        if (excelData) {
            const data = await readXlsxFile(excelData.file);

            const checkExcelRow: CheckedExcelData = {
                excelData: excelData,
                name: excelData.file.name,
                error: false,
                clientIdCheck: true,
                agencyIdCheck: true,
                locationIdCheck: true,
                rowError: [],
                index: index + 1,
                startRowIndex: rowIndex,
                endRowIndex: rowIndex + data.length - 4,
            };

            rowIndex = checkExcelRow.endRowIndex + 1;

            try {
                const clientName = String(data[0][1]);
                let clientId: string;

                if (
                    clientName === undefined ||
                    clientName === "undefined" ||
                    clientName == "null" ||
                    clientName === null ||
                    apiResponseDataStore[`${clientName}`] === "error"
                ) {
                    clientId = "";
                    checkExcelRow.clientIdCheck = false;
                } else {
                    clientId = apiResponseDataStore[`${clientName}`];
                }

                const agencyName = String(data[0][5]);
                let agencyId: string;

                if (
                    agencyName === undefined ||
                    agencyName === "undefined" ||
                    agencyName == "null" ||
                    agencyName === null ||
                    apiResponseDataStore[`${agencyName}`] === "error"
                ) {
                    agencyId = "";
                    checkExcelRow.agencyIdCheck = false;
                } else {
                    agencyId = apiResponseDataStore[`${agencyName}`];
                }

                /* 엑셀파일 전체 날짜 */
                const dateRange = parseDateRange(String(data[0][3]));
                const startDateToWork = dateRange.startDate;
                const endDateToWork = dateRange.endDate;

                //!
                /* 엑셀 row 값 하나하나 가져오기 */
                for (let index = 3; index < data.length; index++) {
                    const row = data[index];
                    const rowErrorInfo: string[] = [];

                    const createDeliveryRequest: CreateDeliveryPayload = {
                        clientId,
                        agencyId,
                        dateToWork: formatDate(startDateToWork),
                        pickUpAddress: "",
                        pickUpAction: {
                            startTimeLimited: "",
                            endTimeLimited: "",
                            requirement: "",
                        },
                        pickUpWorkPlace: {
                            name: "",
                            floor: undefined,
                            phone: "",
                            detail: "",
                        },
                        dropOffAddress: "",
                        dropOffAction: {
                            startTimeLimited: "",
                            endTimeLimited: "",
                            requirement: "",
                        },
                        dropOffWorkPlace: {
                            name: "",
                            floor: undefined,
                            phone: "",
                            detail: "",
                        },
                        load: {
                            name: "",
                            quantity: 0,
                        },
                    };

                    /* location Id 탐색  */
                    if (!!!row[3] || !!!row[10]) {
                        if (!!!row[3]) {
                            rowErrorInfo.push(`[${index + 1}행 D열] 주소 정보 미입력`);
                            checkExcelRow.error = true;
                        }

                        if (!!!row[10]) {
                            rowErrorInfo.push(`[${index + 1}행 K열] 주소 정보 미입력`);
                            checkExcelRow.error = true;
                        }
                    } else {
                        createDeliveryRequest.pickUpAddress = String(row[3]);
                        createDeliveryRequest.dropOffAddress = String(row[10]);
                    }

                    /* 요청시간 오류처리 */

                    let pickUpActionTimeLimited;
                    let dropOffActionTimeLimited;

                    if (row[5] instanceof Date && row[5]) {
                        pickUpActionTimeLimited = new Date(String(row[5])).toISOString();
                    } else {
                        pickUpActionTimeLimited = "null";
                    }
                    if (row[12] instanceof Date && row[12]) {
                        dropOffActionTimeLimited = new Date(String(row[12])).toISOString();
                    } else {
                        dropOffActionTimeLimited = "null";
                    }
                    if (pickUpActionTimeLimited !== "null") {
                        pickUpActionTimeLimited = pickUpActionTimeLimited.replace(
                            pickUpActionTimeLimited.substr(0, 10),
                            createDeliveryRequest.dateToWork
                        );
                        pickUpActionTimeLimited = pickUpActionTimeLimited.replace(
                            pickUpActionTimeLimited.substr(17, 20),
                            "00.000+09:00"
                        );
                    }
                    if (dropOffActionTimeLimited !== "null") {
                        dropOffActionTimeLimited = dropOffActionTimeLimited.replace(
                            dropOffActionTimeLimited.substr(0, 10),
                            createDeliveryRequest.dateToWork
                        );
                        dropOffActionTimeLimited = dropOffActionTimeLimited.replace(
                            dropOffActionTimeLimited.substr(17, 20),
                            "00.000+09:00"
                        );
                    }

                    createDeliveryRequest.pickUpWorkPlace.name =
                        String(row[2]) === "null"
                            ? undefined
                            : removeInvalidSpecialCharacters(row[2]);

                    /* 필수 데이터 상세주소 */
                    createDeliveryRequest.pickUpWorkPlace.detail =
                        removeInvalidSpecialCharacters(row[4]);

                    /* 요청 시간 */
                    createDeliveryRequest.pickUpAction.startTimeLimited =
                        pickUpActionTimeLimited;
                    createDeliveryRequest.pickUpAction.endTimeLimited =
                        pickUpActionTimeLimited;
                    createDeliveryRequest.pickUpWorkPlace.phone =
                        String(row[6]) === "null"
                            ? undefined
                            : removeInvalidSpecialCharacters(row[6]);
                    createDeliveryRequest.pickUpAction.requirement =
                        String(row[7]) === "null"
                            ? undefined
                            : removeInvalidSpecialCharacters(row[7]);
                    createDeliveryRequest.dropOffWorkPlace.name =
                        String(row[9]) === "null"
                            ? undefined
                            : removeInvalidSpecialCharacters(row[9]);
                    /* 필수 데이터 상세주소 */
                    createDeliveryRequest.dropOffWorkPlace.detail =
                        removeInvalidSpecialCharacters(row[11]) || "";
                    /* 요청 시간 */
                    createDeliveryRequest.dropOffAction.startTimeLimited =
                        dropOffActionTimeLimited;
                    createDeliveryRequest.dropOffAction.endTimeLimited =
                        dropOffActionTimeLimited;
                    createDeliveryRequest.dropOffWorkPlace.phone =
                        String(row[13]) === "null"
                            ? undefined
                            : removeInvalidSpecialCharacters(row[13]);
                    createDeliveryRequest.dropOffAction.requirement =
                        String(row[14]) === "null"
                            ? undefined
                            : removeInvalidSpecialCharacters(row[14]);
                    createDeliveryRequest.dropOffWorkPlace.floor =
                        row[15] === undefined ? undefined : Number(row[15]);
                    /* 물건 종류 */
                    createDeliveryRequest.load.name =
                        String(row[16]) === "null"
                            ? ""
                            : removeInvalidSpecialCharacters(row[16]);
                    createDeliveryRequest.load.quantity = row[17] === null ? undefined : Number(row[17]);

                    /* 물류 유형 (옵셔널, S열 = row[18]) - 기존 양식 호환 위해 미입력 시 NULL */
                    const logisticsType = parseLogisticsTypeCell(row[18]);
                    if (logisticsType) {
                        createDeliveryRequest.logisticsType = logisticsType;
                    }

                    if (createDeliveryRequest.pickUpWorkPlace.detail === "null") {
                        rowErrorInfo.push(
                            `[${index + 1}행 E열] 상차 정보 > 상세 주소 오류`
                        );
                        checkExcelRow.error = true;
                    }
                    if (createDeliveryRequest.pickUpAction.startTimeLimited == "null") {
                        rowErrorInfo.push(
                            `[${index + 1}행 F열] 상차 정보 > 요청 시간 오류`
                        );
                        checkExcelRow.error = true;
                    }
                    if (createDeliveryRequest.pickUpAction.endTimeLimited == "null") {
                        rowErrorInfo.push(
                            `[${index + 1}행 F열] 상차 정보 > 요청 시간 오류`
                        );
                        checkExcelRow.error = true;
                    }

                    if (createDeliveryRequest.dropOffWorkPlace.detail === "null") {
                        rowErrorInfo.push(
                            `[${index + 1}행 L열] 전달 정보 > 상세 주소 오류`
                        );
                        checkExcelRow.error = true;
                    }
                    if (createDeliveryRequest.dropOffAction.startTimeLimited === "null") {
                        rowErrorInfo.push(
                            `[${index + 1}행 M열] 전달 정보 > 요청 시간 오류`
                        );
                        checkExcelRow.error = true;
                    }
                    if (createDeliveryRequest.dropOffAction.endTimeLimited === "null") {
                        rowErrorInfo.push(
                            `[${index + 1}행 M열] 전달 정보 > 요청 시간 오류`
                        );
                        checkExcelRow.error = true;
                    }

                    // if (createDeliveryRequest.load.name === "null") {
                    //   rowErrorInfo.push(`[${index + 1}행 Q열] 요청 물건 > 물건 종류 오류`);
                    //   checkExcelRow.error = true;
                    // }
                    if (
                        createDeliveryRequest.load.quantity !== undefined &&
                        (!createDeliveryRequest.load.quantity) &&
                        createDeliveryRequest.load.quantity !== 0
                    ) {
                        rowErrorInfo.push(`[${index + 1}행 R열] 요청물건 개수 형식 오류`);
                        checkExcelRow.error = true;
                    }

                    checkExcelRow.rowError = [...checkExcelRow.rowError].concat(
                        rowErrorInfo
                    );

                    const currentFileStartIndex = createDeliveryRequestData.length;  // 현재 파일 데이터의 시작 인덱스 저장

                    if (rowErrorInfo.length == 0 && checkExcelRow.clientIdCheck === true) {
                        createDeliveryRequestData.push(createDeliveryRequest);
                    }

                    // 날짜 범위에 따른 추가 배송 요청 생성
                    if (startDateToWork < endDateToWork) {
                        const currentFileRequests = createDeliveryRequestData.slice(currentFileStartIndex);  // 현재 파일의 요청만 선택
                        const additionalRequests: CreateDeliveryPayload[] = [];
                        const dayInMilliseconds = 24 * 60 * 60 * 1000;

                        // 시작일 다음날부터 종료일까지 순회
                        for (
                            let currentDate = new Date(startDateToWork.getTime() + dayInMilliseconds);
                            currentDate <= endDateToWork;
                            currentDate = new Date(currentDate.getTime() + dayInMilliseconds)
                        ) {
                            currentFileRequests.forEach(request => {  // 현재 파일의 요청만 복제
                                const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                                let newPickUpActionTimeLimited = request.pickUpAction.startTimeLimited;
                                let newDropOffActionTimeLimited = request.dropOffAction.startTimeLimited;

                                if (newPickUpActionTimeLimited !== "null") {
                                    newPickUpActionTimeLimited = newPickUpActionTimeLimited.replace(
                                        newPickUpActionTimeLimited.substr(0, 10),
                                        formattedDate
                                    );
                                    newPickUpActionTimeLimited = newPickUpActionTimeLimited.replace(
                                        newPickUpActionTimeLimited.substr(17, 20),
                                        "00.000+09:00"
                                    );
                                }

                                if (newDropOffActionTimeLimited !== "null") {
                                    newDropOffActionTimeLimited = newDropOffActionTimeLimited.replace(
                                        newDropOffActionTimeLimited.substr(0, 10),
                                        formattedDate
                                    );
                                    newDropOffActionTimeLimited = newDropOffActionTimeLimited.replace(
                                        newDropOffActionTimeLimited.substr(17, 20),
                                        "00.000+09:00"
                                    );
                                }

                                const newRequest = {
                                    ...request,
                                    dateToWork: formattedDate,
                                    pickUpAction: {
                                        ...request.pickUpAction,
                                        startTimeLimited: newPickUpActionTimeLimited,
                                        endTimeLimited: newPickUpActionTimeLimited
                                    },
                                    dropOffAction: {
                                        ...request.dropOffAction,
                                        startTimeLimited: newDropOffActionTimeLimited,
                                        endTimeLimited: newDropOffActionTimeLimited
                                    }
                                };
                                additionalRequests.push(newRequest);
                            });
                        }

                        // 추가 요청들을 기존 배열에 합침
                        createDeliveryRequestData.push(...additionalRequests);
                    }
                }

                checkExcelSheetData.push(checkExcelRow);
            } catch (error) {
                console.log(error);
            }
        }
    }

    return {
        checkExcelSheetData,
        createDeliveryRequestData,
    };
};
